/**
 * The presentation mode: the application is shown to someone (a demo, a shared screen) without them learning
 * the figures. While it is on, an amount reads « ••• € », a figure of a dashboard « ••• », the value axes of
 * the charts lose their scale and the bodies of mails and notes are blurred.
 *
 * It is a mask on the screen, not an access rule: the data still reaches this browser. A person with their own
 * access needs a role the server enforces (PLAN-MULTI-TENANT § 5).
 *
 * Read once before the first render, from this browser's storage, and switching reloads the page: no component
 * has to subscribe, a reload during a demo keeps the mask, and no real figure is drawn before the mask is known.
 * Off, every helper returns its input untouched: the application is exactly what it was without the mode.
 */
import { logWarn } from './logger';

const STORAGE_KEY = 'aq-presentation-mode';

/** What a hidden figure reads. */
export const MASK = '•••';

/**
 * A number as the application writes one: digits with their French separators (`\s` covers the no-break and the
 * narrow no-break spaces Intl writes, comma, dot), a sign, and a « k »/« M » multiple not followed by a letter, so
 * that the multiple goes with the number (« 3,2 k€ », « (12k) », « 12 kEUR »), never « 12 mois » or « 3 km ».
 */
const NUMBER = /[-+\u2212]?\d(?:[\d\s.,]*\d)?(?:\s?[kKM](?=EUR|[^A-Za-z\u00c0-\u00ff]|$))?/g;

let active = false;

export function parsePresentationMode(raw: string | null): boolean {
  return raw === 'on';
}

/** Every number of a text written for the screen replaced by « ••• », its unit and words kept. Pure. */
export function maskFigures(text: string, on: boolean): string {
  if (!on) return text;
  // A multiple stuck to its unit (« 3,2 k€ ») leaves the unit stuck to the mask: a space keeps every scale alike.
  return text.replace(NUMBER, (number, offset: number) => {
    const next = text.charAt(offset + number.length);
    return /[kKM]$/.test(number) && next !== '' && !/\s/.test(next) && /[A-Za-z\u20ac$]/.test(next) ? `${MASK} ` : MASK;
  });
}

/**
 * A figure already formatted for the screen (« 12 400 € », « 450 € HT/mois », « 38 clients »): « ••• € »,
 * « ••• € HT/mois », « ••• clients » while the mode is on, the text as is otherwise. Only where a value is shown:
 * never on a value an input edits, a file exports or a request sends.
 */
export function sensitive(text: string): string {
  return maskFigures(text, active);
}

export function isPresentationMode(): boolean {
  return active;
}

/** Once, before the first render: reads the stored choice and marks <html> for the blur of free texts (index.css). */
export function initPresentationMode(): boolean {
  try {
    active = parsePresentationMode(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Storage blocked (private window, policy): the mode cannot be kept, so it stays off.
    active = false;
  }
  if (active) document.documentElement.dataset.presentation = 'on';
  return active;
}

/** Switches the mode and reloads the page under it; false, and nothing changes, when this browser refuses to keep it. */
export function setPresentationMode(on: boolean): boolean {
  try {
    if (on) window.localStorage.setItem(STORAGE_KEY, 'on');
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    logWarn('presentation', 'storage refused the presentation mode', { on, err });
    return false;
  }
  window.location.reload();
  return true;
}
